/**
 * /producao — Painel de Produção (Sprint P1 — integração real).
 *
 * 3 zonas em coluna de altura fixa (brief "Tela — Painel de Produção"):
 *   Zona 1 — EntityHeader (título + contador de ambientes).
 *   Zona 2 — KpiStrip (5 KPIs de /api/producao/kpis) + barra de filtros
 *            (SeletorFases + busca + limpar + toggle Board⇄Tabela).
 *   Zona 3 — Board por ETAPA (23 colunas do contrato, agrupadas por fase,
 *            altura 100% + scroll por coluna) OU Tabela (table-layout fixed,
 *            zero scroll lateral). Mesmo estado de filtro nos dois modos.
 *
 * Fontes: GET /api/producao/board (board) e GET /api/ambientes-producao
 * (tabela — ?busca= server-side + ?atrasados= do KPI "Em atraso"). No modo
 * board a busca filtra client-side (o endpoint do board não aceita busca).
 * Clique no KPI "Buffer crítico" filtra zona vermelha client-side nos dois
 * modos; "Em atraso" muda para o modo tabela (filtro é do endpoint Lista).
 *
 * Lote C (§2.4 — card-lote ATÔMICO): de PLANO_CORTE a USINAGEM as entradas
 * das colunas podem ser `{tipo: "lote", ...}` — renderizadas com LoteCard e
 * arrastáveis com a MESMA física 1-adiante (concluir a etapa do LOTE
 * cascateia em bloco aos membros; resposta {concluidos, pulados}).
 * Contadores continuam POR AMBIENTE (o card-lote soma `ambientes_count`).
 * Card-ambiente com `sem_lote` ganha selo warning; cards de F6+ com
 * `lote_numero` ganham chip LT clicável. Board sem `tipo:"lote"` (backend
 * anterior) renderiza como sempre — degradação limpa.
 *
 * Persistência: modo em `prod.painel.modo`, range de fases em
 * `prod.painel.fases` (localStorage).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import AmbienteCard from "../../components/producao/AmbienteCard";
import LoteCard from "../../components/producao/LoteCard";
import SeletorFases from "../../components/producao/SeletorFases";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  FASE_INFO,
  FASES_PAINEL,
  boardPorEtapa,
  concluirEtapa,
  concluirEtapaLote,
  kpisPainel,
  listarAmbientes,
  obterAmbiente,
  permissaoOperarFase,
} from "../../services/producaoV2";
import { normalizeText } from "../../utils/data";
import {
  ErrorMsg,
  Pill,
  SearchInput,
  SecondaryButton,
} from "../Orcamentos/styles";
import { useCheckpointsBipagem } from "./_shared/useCheckpointsBipagem";

const CHAVE_MODO = "prod.painel.modo";
const CHAVE_FASES = "prod.painel.fases";
const TOTAL_FASES = FASES_PAINEL.length;
const DEBOUNCE_BUSCA_MS = 350;

const INDICE_POR_FASE = FASES_PAINEL.reduce((acc, fase, idx) => {
  acc[fase.codigo] = idx;
  return acc;
}, {});

const COR_ZONA = {
  verde: "var(--success-solid)",
  amarelo: "var(--warning-solid)",
  vermelho: "var(--danger-solid)",
};

function lerModoInicial() {
  try {
    return window.localStorage.getItem(CHAVE_MODO) === "tabela"
      ? "tabela"
      : "board";
  } catch {
    return "board";
  }
}

function lerFaixaInicial() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(CHAVE_FASES) || "null");
    if (
      raw
      && Number.isInteger(raw.start)
      && Number.isInteger(raw.end)
      && raw.start >= 0
      && raw.end < TOTAL_FASES
      && raw.start <= raw.end
    ) {
      return raw;
    }
  } catch {
    /* valor corrompido — cai no default */
  }
  return { start: 0, end: TOTAL_FASES - 1 };
}

// ---------------------------------------------------------------------------
// Entradas do board (Lote C): a lista `etapa.ambientes` pode misturar cards
// de AMBIENTE (shape de sempre) e cards de LOTE ({tipo: "lote", ...}).
// ---------------------------------------------------------------------------

function ehEntradaLote(entrada) {
  return entrada?.tipo === "lote";
}

/** Nº de ambientes que a entrada representa (card-lote conta os membros). */
function ambientesRepresentados(entrada) {
  if (!ehEntradaLote(entrada)) return 1;
  return entrada.ambientes_count ?? (entrada.ambientes || []).length;
}

/** Id estável da entrada p/ drag/shake/key (lote nunca colide com ambiente). */
function idEntrada(entrada) {
  return ehEntradaLote(entrada) ? `lote-${entrada.lote?.id}` : entrada.id;
}

/** Como a entrada aparece em mensagens ("o lote LT-UN1-4" / "AMB-UN1-12"). */
function rotuloEntrada(entrada) {
  return ehEntradaLote(entrada)
    ? `o lote ${entrada.lote?.numero || ""}`.trim()
    : entrada.numero;
}

// ---------------------------------------------------------------------------
// Styled — layout de altura fixa (flex column + min-height:0 encadeado)
// ---------------------------------------------------------------------------

const PainelPagina = styled.div`
  width: 100%;
  max-width: var(--page-max-width, 1440px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* Altura útil = viewport - topbar - banner MFA - padding superior do
     <main> global (space-7) - 20px de respiro inferior. O padding inferior
     do <main> (space-12 — system.css) é reaproveitado via margin negativa
     para dar mais altura às colunas do board (só nesta tela). */
  height: calc(
    100vh - var(--topbar-height) - var(--mfa-banner-h, 0px)
    - var(--space-7) - 20px
  );
  margin-bottom: calc(20px - var(--space-12));
  min-height: 520px;
`;

const BarraFiltros = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
`;

const AreaSeletor = styled.div`
  flex: 1 1 420px;
  min-width: 320px;
`;

const ControlesDireita = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 2px;
`;

const GrupoModo = styled.div`
  display: inline-flex;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-md, 8px);
  padding: 2px;
  flex-shrink: 0;
`;

const ChipModo = styled.button`
  border: none;
  background: ${({ $ativo }) => ($ativo ? "var(--surface)" : "transparent")};
  color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--ink-3)")};
  box-shadow: ${({ $ativo }) => ($ativo ? "var(--shadow-1)" : "none")};
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 6px;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const BotaoGhost = styled.button`
  border: none;
  background: none;
  color: var(--accent);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    background: var(--accent-soft);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Zona3 = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
`;

// ---------- Board ----------

const BoardScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  gap: 18px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 6px;
`;

const GrupoFase = styled.section`
  flex: 0 0 auto;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FaixaFase = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--accent);
  background: var(--accent-soft);
  border-radius: var(--radius-pill, 999px);
  padding: 4px 14px;

  .contador {
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }
`;

const ColunasDaFase = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  gap: 14px;
`;

const ColunaEtapa = styled.div`
  flex: 0 0 290px;
  width: 290px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface-2);
  border-radius: 16px;
  padding: 10px;

  /* Durante o drag: a coluna imediatamente seguinte é o ÚNICO alvo válido
     (drag 1-adiante = concluir a etapa atual — D1.1). Dica tracejada;
     sólida quando o ponteiro está sobre ela. */
  ${({ $alvoProximo }) =>
    $alvoProximo &&
    css`
      outline: 2px dashed var(--accent);
      outline-offset: -2px;
    `}
  ${({ $alvoAtivo }) =>
    $alvoAtivo &&
    css`
      outline-style: solid;
      background: var(--accent-soft);
    `}
`;

const ColunaHeader = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
  padding: 2px 6px 8px;

  .titulo {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .contador {
    font-variant-numeric: tabular-nums;
    font-weight: 500;
    color: var(--ink-3);
  }
`;

/* Affordance discreta: você OPERA esta coluna (permissão da fase). */
const DotOperavel = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
  display: inline-block;
  flex-shrink: 0;
`;

const shakeAnim = keyframes`
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(3px); }
  30%, 50%, 70% { transform: translateX(-3px); }
  40%, 60% { transform: translateX(3px); }
`;

/**
 * Wrapper de drag do card (pointer events — HTML5 DnD não convive com o
 * scroll lateral do board; mesmo padrão do SeletorFases). touch-action
 * pan-y preserva o scroll vertical da coluna no toque; o drag ativa no
 * gesto horizontal.
 */
const CardArrastavel = styled.div`
  ${({ $arrastavel }) =>
    $arrastavel &&
    css`
      cursor: grab;
      touch-action: pan-y;
      user-select: none;
      -webkit-user-select: none;
    `}
  ${({ $origem }) =>
    $origem &&
    css`
      opacity: 0.35;
    `}
  ${({ $shake }) =>
    $shake &&
    css`
      animation: ${shakeAnim} 0.45s ease-in-out;
    `}
`;

/* Clone flutuante do card durante o arraste (posicionado via transform). */
const GhostDrag = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1200;
  pointer-events: none;
  opacity: 0.92;
  filter: drop-shadow(0 12px 24px rgba(15, 20, 40, 0.28));
`;

const ListaCards = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 2px;
`;

const ColunaVazia = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--ink-4);
  font-size: 12px;
  padding: 12px;
`;

// ---------- Tabela ----------

const ZonaTabela = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
`;

const TabelaFixa = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--surface-2);
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-3);
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
  }

  td {
    padding: 10px 12px;
    border-top: 1px solid var(--border-subtle);
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr {
    cursor: pointer;
  }
  tbody tr:hover {
    background: var(--surface-hover);
  }
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const DotZona = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  background: ${({ $zona }) => COR_ZONA[$zona] || COR_ZONA.verde};
`;

const RodapeConfirmacao = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const EstadoCentral = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--ink-2);
  font-size: 14px;
  text-align: center;
`;

const LinhaErro = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

// ---------------------------------------------------------------------------

export default function Painel() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  const toast = useToast();
  const checkpoints = useCheckpointsBipagem();

  const [modo, setModo] = useState(lerModoInicial);
  const [faixa, setFaixa] = useState(lerFaixaInicial);
  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [filtroAtrasados, setFiltroAtrasados] = useState(false);
  const [filtroBufferCritico, setFiltroBufferCritico] = useState(false);

  const [board, setBoard] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const [lista, setLista] = useState(null);
  const [erroLista, setErroLista] = useState(null);

  // ---- drag 1-adiante (Lote B — I-9/D1.1) ----
  // Estado mutável do arraste (sem re-render por frame — padrão do
  // SeletorFases); dragVisual só muda quando algo visível muda.
  const dragInfoRef = useRef(null);
  const ghostRef = useRef(null);
  const posGhostRef = useRef({ x: 0, y: 0 });
  const colunaRefs = useRef(new Map());
  const suprimirCliqueRef = useRef(false);
  const shakeTimer = useRef(null);
  const [dragVisual, setDragVisual] = useState(null);
  const [shakeId, setShakeId] = useState(null);
  // {entrada, coluna, proxima} -> modal "Concluir {etapa} de {entrada}?"
  // (entrada = ambiente OU card-lote — Lote C conclui em bloco).
  const [confirmacao, setConfirmacao] = useState(null);

  useEffect(
    () => () => {
      if (shakeTimer.current) clearTimeout(shakeTimer.current);
    },
    [],
  );

  // ---- persistência ----
  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_MODO, modo);
    } catch { /* storage indisponível */ }
  }, [modo]);
  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_FASES, JSON.stringify(faixa));
    } catch { /* storage indisponível */ }
  }, [faixa]);

  // ---- debounce da busca ----
  useEffect(() => {
    const t = setTimeout(
      () => setBuscaDebounced(busca.trim()),
      DEBOUNCE_BUSCA_MS,
    );
    return () => clearTimeout(t);
  }, [busca]);

  // ---- carga board + kpis (fonte do modo board e dos KPIs) ----
  const carregarPainel = useCallback(async () => {
    setErro(null);
    setCarregando(true);
    try {
      const [b, k] = await Promise.all([
        boardPorEtapa(request),
        kpisPainel(request),
      ]);
      setBoard(b);
      setKpis(k);
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar o painel.");
    } finally {
      setCarregando(false);
    }
  }, [request]);

  useEffect(() => {
    carregarPainel();
  }, [carregarPainel]);

  // ---- carga da tabela (busca server-side + filtro atrasados) ----
  const carregarLista = useCallback(async () => {
    if (modo !== "tabela") return;
    setErroLista(null);
    try {
      const resposta = await listarAmbientes(request, {
        busca: buscaDebounced || undefined,
        atrasados: filtroAtrasados || undefined,
      });
      setLista(Array.isArray(resposta) ? resposta : resposta?.results || []);
    } catch (err) {
      setErroLista(err?.message || "Não foi possível carregar a lista.");
      setLista(null);
    }
  }, [request, modo, buscaDebounced, filtroAtrasados]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  // ---- filtros client-side compartilhados ----
  const needle = useMemo(() => normalizeText(buscaDebounced), [buscaDebounced]);

  const passaFiltrosCard = useCallback(
    (entrada) => {
      // Card-lote (Lote C): buffer = pior buffer entre os membros; busca
      // casa o número do lote OU qualquer membro (quando não-cega).
      if (ehEntradaLote(entrada)) {
        if (filtroBufferCritico && entrada.buffer_pior?.zona !== "vermelho") {
          return false;
        }
        if (needle) {
          const membros = (entrada.ambientes || [])
            .map(
              (m) =>
                `${m.numero || ""} ${m.nome || ""} ${m.pedido_numero || ""} `
                + `${m.cliente_nome || ""}`,
            )
            .join(" ");
          const alvo = normalizeText(
            `${entrada.lote?.numero || ""} ${membros}`,
          );
          if (!alvo.includes(needle)) return false;
        }
        return true;
      }
      const a = entrada;
      if (filtroBufferCritico && a?.buffer?.zona !== "vermelho") return false;
      if (needle) {
        const alvo = normalizeText(
          `${a.numero} ${a.nome} ${a.pedido_numero} ${a.cliente_nome || ""}`,
        );
        if (!alvo.includes(needle)) return false;
      }
      return true;
    },
    [filtroBufferCritico, needle],
  );

  // Board agrupado por fase, com o range do seletor aplicado nas COLUNAS.
  const gruposVisiveis = useMemo(() => {
    if (!board?.etapas) return [];
    const porFase = new Map();
    const etapasOrdenadas = [...board.etapas].sort(
      (a, b) => (a.ordem || 0) - (b.ordem || 0),
    );
    for (const etapa of etapasOrdenadas) {
      const idx = INDICE_POR_FASE[etapa.fase];
      if (idx === undefined || idx < faixa.start || idx > faixa.end) continue;
      if (!porFase.has(etapa.fase)) {
        porFase.set(etapa.fase, {
          fase: etapa.fase,
          rotulo: FASE_INFO[etapa.fase]?.rotulo || etapa.fase,
          colunas: [],
        });
      }
      porFase.get(etapa.fase).colunas.push({
        ...etapa,
        ambientes: (etapa.ambientes || []).filter(passaFiltrosCard),
      });
    }
    return [...porFase.values()];
  }, [board, faixa, passaFiltrosCard]);

  // Contador POR AMBIENTE representado (o card-lote soma ambientes_count —
  // o board nunca "encolhe" quando os ambientes agrupam num lote).
  const totalAmbientesVisiveis = useMemo(
    () =>
      gruposVisiveis.reduce(
        (soma, g) =>
          soma
          + g.colunas.reduce(
            (s, c) =>
              s
              + c.ambientes.reduce(
                (x, entrada) => x + ambientesRepresentados(entrada),
                0,
              ),
            0,
          ),
        0,
      ),
    [gruposVisiveis],
  );

  // Linhas da tabela: range de fases + buffer crítico client-side
  // (busca/atrasados já vieram do servidor).
  const linhasVisiveis = useMemo(() => {
    if (!Array.isArray(lista)) return [];
    const rangeCompleto = faixa.start === 0 && faixa.end === TOTAL_FASES - 1;
    return lista.filter((a) => {
      const idx = INDICE_POR_FASE[a.fase];
      if (idx === undefined) {
        // Marcos (PEDIDO_CRIADO/CARREGADO) só aparecem na visão geral.
        if (!rangeCompleto) return false;
      } else if (idx < faixa.start || idx > faixa.end) {
        return false;
      }
      if (filtroBufferCritico && a?.buffer?.zona !== "vermelho") return false;
      return true;
    });
  }, [lista, faixa, filtroBufferCritico]);

  // Etapa corrente por ambiente (o serializer Lista não expõe — deriva do
  // board; membro de card-lote herda a etapa do LOTE — modelo atômico).
  const etapaPorAmbiente = useMemo(() => {
    const mapa = {};
    board?.etapas?.forEach((etapa) => {
      (etapa.ambientes || []).forEach((entrada) => {
        if (ehEntradaLote(entrada)) {
          (entrada.ambientes || []).forEach((m) => {
            if (m?.id) mapa[m.id] = etapa.label;
          });
        } else {
          mapa[entrada.id] = etapa.label;
        }
      });
    });
    return mapa;
  }, [board]);

  // ---- drag 1-adiante: helpers ----

  // Coluna imediatamente seguinte no roteiro (por ordem 1..23) — único
  // alvo válido do drag (soltar lá = concluir a etapa ATUAL do card).
  const proximaPorCodigo = useMemo(() => {
    const mapa = {};
    const ordenadas = [...(board?.etapas || [])].sort(
      (a, b) => (a.ordem || 0) - (b.ordem || 0),
    );
    ordenadas.forEach((etapa, idx) => {
      mapa[etapa.codigo] = ordenadas[idx + 1] || null;
    });
    return mapa;
  }, [board]);

  // Permissão de operar a coluna = producao_fase:operar_f{n} da fase da
  // etapa (Master bypass no hasPermission).
  const podeOperarColuna = useCallback(
    (coluna) => {
      const permissao = permissaoOperarFase(coluna.fase);
      return Boolean(permissao) && hasPermission(permissao);
    },
    [hasPermission],
  );

  const registrarColunaRef = useCallback((codigo, el) => {
    if (el) colunaRefs.current.set(codigo, el);
    else colunaRefs.current.delete(codigo);
  }, []);

  const colunaEmPonto = useCallback((x, y) => {
    for (const [codigo, el] of colunaRefs.current) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        return codigo;
      }
    }
    return null;
  }, []);

  const dispararShake = useCallback((id) => {
    setShakeId(id);
    if (shakeTimer.current) clearTimeout(shakeTimer.current);
    shakeTimer.current = setTimeout(() => setShakeId(null), 500);
  }, []);

  const moverGhost = useCallback((x, y) => {
    posGhostRef.current = { x, y };
    if (ghostRef.current) {
      ghostRef.current.style.transform = `translate(${x}px, ${y}px) rotate(2deg)`;
    }
  }, []);

  // O ghost monta DEPOIS do 1º pointermove que ativa o drag — o callback
  // ref aplica a última posição conhecida na montagem.
  const setGhostEl = useCallback((el) => {
    ghostRef.current = el;
    if (el) {
      const { x, y } = posGhostRef.current;
      el.style.transform = `translate(${x}px, ${y}px) rotate(2deg)`;
    }
  }, []);

  const aoPointerDownCard = useCallback(
    (e, entrada, coluna) => {
      if (e.button !== undefined && e.button !== 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      dragInfoRef.current = {
        entrada,
        coluna,
        proxima: proximaPorCodigo[coluna.codigo] || null,
        startX: e.clientX,
        startY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        largura: rect.width,
        pointerType: e.pointerType,
        ativo: false,
        sobre: null,
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [proximaPorCodigo],
  );

  const aoPointerMoveCard = useCallback(
    (e) => {
      const d = dragInfoRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.ativo) {
        const limiar = d.pointerType === "touch" ? 10 : 5;
        if (Math.abs(dx) < limiar && Math.abs(dy) < limiar) return;
        // Toque com gesto vertical dominante = scroll da coluna, não drag.
        if (d.pointerType === "touch" && Math.abs(dy) > Math.abs(dx)) {
          dragInfoRef.current = null;
          return;
        }
        d.ativo = true;
        setDragVisual({
          entrada: d.entrada,
          coluna: d.coluna,
          proximaCodigo: d.proxima?.codigo || null,
          largura: d.largura,
          sobre: null,
        });
      }
      moverGhost(e.clientX - d.offsetX, e.clientY - d.offsetY);
      const sobre = colunaEmPonto(e.clientX, e.clientY);
      if (sobre !== d.sobre) {
        d.sobre = sobre;
        setDragVisual((v) => (v ? { ...v, sobre } : v));
      }
    },
    [colunaEmPonto, moverGhost],
  );

  const aoPointerUpCard = useCallback(() => {
    const d = dragInfoRef.current;
    dragInfoRef.current = null;
    if (!d || !d.ativo) return; // clique seco — segue para o onClick do card
    suprimirCliqueRef.current = true;
    setDragVisual(null);
    const { entrada, coluna, proxima, sobre } = d;
    // Soltar fora do board ou de volta na coluna de origem = cancelar.
    if (!sobre || sobre === coluna.codigo) return;
    if (proxima && sobre === proxima.codigo) {
      if (checkpoints.has(coluna.codigo)) {
        // Etapa-checkpoint: o modal nem abre (§2.5) — o backend também
        // rejeita com 400 se chamado (fallback coberto no modal).
        toast.warning(
          `${coluna.label} é checkpoint de bipagem — ${rotuloEntrada(entrada)} `
          + "conclui peça a peça no Terminal.",
        );
        dispararShake(idEntrada(entrada));
        return;
      }
      setConfirmacao({ entrada, coluna, proxima });
      return;
    }
    // Pular colunas / arrastar para trás não soltam no MVP (D1.2) — o
    // card volta com shake sutil, sem ação.
    dispararShake(idEntrada(entrada));
  }, [checkpoints, dispararShake, toast]);

  const aoPointerCancelCard = useCallback(() => {
    dragInfoRef.current = null;
    setDragVisual(null);
  }, []);

  // Depois de um drag, o browser ainda dispara o click no card — suprime.
  const aoClickCaptureCard = useCallback((e) => {
    if (suprimirCliqueRef.current) {
      suprimirCliqueRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const contador =
    modo === "board" ? totalAmbientesVisiveis : linhasVisiveis.length;

  const temFiltroAtivo =
    Boolean(buscaDebounced)
    || filtroAtrasados
    || filtroBufferCritico
    || faixa.start !== 0
    || faixa.end !== TOTAL_FASES - 1;

  const limparFiltros = () => {
    setBusca("");
    setFiltroAtrasados(false);
    setFiltroBufferCritico(false);
    setFaixa({ start: 0, end: TOTAL_FASES - 1 });
  };

  // ---- KPIs (clique = recorte rápido) ----
  const itensKpi = useMemo(() => {
    const k = kpis || {};
    return [
      {
        label: "WIP",
        value: k.wip ?? "–",
        sub: "ambientes em execução",
      },
      {
        label: "Carregam hoje",
        value: k.carregam_hoje ?? "–",
        sub: "data-alvo = hoje",
      },
      {
        label: "Em atraso",
        value: k.em_atraso > 0 ? k.em_atraso : "–",
        sub: filtroAtrasados
          ? "filtro ativo — clique para limpar"
          : "clique para filtrar (tabela)",
        onClick: () => {
          setFiltroAtrasados((v) => !v);
          setModo("tabela");
        },
      },
      {
        label: "Anomalias",
        value: k.anomalias > 0 ? k.anomalias : "–",
        sub: "eventos abertos (medição no P3)",
      },
      {
        label: "Buffer crítico",
        value: k.buffer_critico > 0 ? k.buffer_critico : "–",
        sub: filtroBufferCritico
          ? "filtro ativo — clique para limpar"
          : "zona vermelha — clique para filtrar",
        onClick: () => setFiltroBufferCritico((v) => !v),
      },
    ];
  }, [kpis, filtroAtrasados, filtroBufferCritico]);

  const abrirAmbiente = (id) => navigate(`/producao/ambientes/${id}`);

  const boardVazioGlobal = !carregando && board && board.total_ambientes === 0;

  return (
    <PainelPagina>
      <EntityHeader
        sticky={false}
        title="Produção"
        subtitle="Painel de Produção — board por etapa"
        badges={[{ label: `${contador} ambientes`, tone: "neutral" }]}
      />

      <KpiStrip items={itensKpi} />

      <BarraFiltros>
        <AreaSeletor>
          <SeletorFases
            fases={FASES_PAINEL}
            value={faixa}
            onChange={(start, end) => setFaixa({ start, end })}
          />
        </AreaSeletor>
        <ControlesDireita>
          <SearchInput
            type="search"
            placeholder="Pedido / cliente / AMB…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            aria-label="Buscar ambiente por pedido, cliente ou código"
            style={{ minWidth: 220, flex: "0 1 260px" }}
          />
          {temFiltroAtivo && (
            <BotaoGhost type="button" onClick={limparFiltros}>
              Limpar
            </BotaoGhost>
          )}
          <GrupoModo role="tablist" aria-label="Modo de visualização">
            <ChipModo
              type="button"
              role="tab"
              aria-selected={modo === "board"}
              $ativo={modo === "board"}
              onClick={() => setModo("board")}
            >
              Board
            </ChipModo>
            <ChipModo
              type="button"
              role="tab"
              aria-selected={modo === "tabela"}
              $ativo={modo === "tabela"}
              onClick={() => setModo("tabela")}
            >
              Tabela
            </ChipModo>
          </GrupoModo>
        </ControlesDireita>
      </BarraFiltros>

      {erro && (
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>
            Não foi possível carregar o painel. {erro}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregarPainel}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      <Zona3>
        {carregando ? (
          <EstadoCentral>Carregando painel…</EstadoCentral>
        ) : erro && !board ? (
          <EstadoCentral>
            Não foi possível carregar o painel — use “Tentar de novo” acima.
          </EstadoCentral>
        ) : boardVazioGlobal ? (
          <EstadoCentral>
            <strong>Nenhum ambiente em produção</strong>
            <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
              Ambientes aparecem aqui quando o pedido entra em produção.
            </span>
          </EstadoCentral>
        ) : modo === "board" ? (
          gruposVisiveis.length === 0 ? (
            <EstadoCentral>
              <span>Nenhuma fase no recorte selecionado.</span>
              <BotaoGhost
                type="button"
                onClick={() => setFaixa({ start: 0, end: TOTAL_FASES - 1 })}
              >
                Ver todas as fases
              </BotaoGhost>
            </EstadoCentral>
          ) : (
            <BoardScroll>
              {gruposVisiveis.map((grupo) => (
                <GrupoFase key={grupo.fase}>
                  <FaixaFase>
                    <span>{grupo.rotulo}</span>
                    <span className="contador">
                      {grupo.colunas.reduce(
                        (s, c) =>
                          s
                          + c.ambientes.reduce(
                            (x, entrada) =>
                              x + ambientesRepresentados(entrada),
                            0,
                          ),
                        0,
                      )}
                    </span>
                  </FaixaFase>
                  <ColunasDaFase>
                    {grupo.colunas.map((coluna) => {
                      const operavel = podeOperarColuna(coluna);
                      const ehCheckpoint = checkpoints.has(coluna.codigo);
                      const temProxima = Boolean(
                        proximaPorCodigo[coluna.codigo],
                      );
                      const ehProxima =
                        dragVisual?.proximaCodigo === coluna.codigo;
                      // Contador POR AMBIENTE mesmo com card-lote na coluna
                      // (o lote conta os membros que representa).
                      const representados = coluna.ambientes.reduce(
                        (s, entrada) => s + ambientesRepresentados(entrada),
                        0,
                      );
                      const temLoteNaColuna =
                        coluna.ambientes.some(ehEntradaLote);
                      return (
                        <ColunaEtapa
                          key={coluna.codigo}
                          ref={(el) => registrarColunaRef(coluna.codigo, el)}
                          $alvoProximo={ehProxima}
                          $alvoAtivo={
                            ehProxima && dragVisual?.sobre === coluna.codigo
                          }
                        >
                          <ColunaHeader>
                            <span className="titulo" title={coluna.label}>
                              {operavel && (
                                <DotOperavel
                                  title={
                                    ehCheckpoint
                                      ? "Você opera esta coluna — etapa-checkpoint: conclui por bipagem no Terminal"
                                      : "Você opera esta coluna — arraste o card até a próxima coluna para concluir a etapa"
                                  }
                                />
                              )}
                              {coluna.label}
                            </span>
                            <span
                              className="contador"
                              title={
                                temLoteNaColuna
                                  ? `${representados} ambiente(s) em `
                                    + `${coluna.ambientes.length} card(s) — `
                                    + "o card-lote conta os ambientes que "
                                    + "representa"
                                  : `${representados} ambiente(s)`
                              }
                            >
                              {representados}
                            </span>
                          </ColunaHeader>
                          {coluna.ambientes.length === 0 ? (
                            <ColunaVazia>Sem ambientes nesta etapa</ColunaVazia>
                          ) : (
                            <ListaCards>
                              {coluna.ambientes.map((entrada) => {
                                const eid = idEntrada(entrada);
                                const ehLote = ehEntradaLote(entrada);
                                // Lote arrasta mesmo com membro congelado
                                // (o backend PULA congelados e lista no
                                // resultado); ambiente congelado não.
                                const arrastavel =
                                  operavel
                                  && temProxima
                                  && (ehLote || !entrada.congelado);
                                const handlersDrag = {
                                  onPointerDown: arrastavel
                                    ? (e) =>
                                        aoPointerDownCard(e, entrada, coluna)
                                    : undefined,
                                  onPointerMove: arrastavel
                                    ? aoPointerMoveCard
                                    : undefined,
                                  onPointerUp: arrastavel
                                    ? aoPointerUpCard
                                    : undefined,
                                  onPointerCancel: arrastavel
                                    ? aoPointerCancelCard
                                    : undefined,
                                };
                                if (ehLote) {
                                  return (
                                    <CardArrastavel
                                      key={eid}
                                      $arrastavel={arrastavel}
                                      $origem={
                                        dragVisual
                                        && idEntrada(dragVisual.entrada)
                                          === eid
                                      }
                                      $shake={shakeId === eid}
                                      title={
                                        arrastavel && ehCheckpoint
                                          ? "Etapa-checkpoint: o lote conclui peça a peça por bipagem no terminal"
                                          : undefined
                                      }
                                      {...handlersDrag}
                                      onClickCapture={aoClickCaptureCard}
                                    >
                                      <LoteCard
                                        densidade="compact"
                                        numero={entrada.lote?.numero}
                                        origem={entrada.lote?.origem}
                                        sobraPercentual={
                                          entrada.lote?.sobra_percentual
                                        }
                                        ambientes={
                                          Array.isArray(entrada.ambientes)
                                            ? entrada.ambientes
                                            : null
                                        }
                                        ambientesCount={ambientesRepresentados(
                                          entrada,
                                        )}
                                        bufferPct={
                                          entrada.buffer_pior?.pct ?? 0
                                        }
                                        bufferZona={
                                          entrada.buffer_pior?.zona || "verde"
                                        }
                                        pecasTotal={entrada.pecas_total}
                                        congeladosCount={
                                          entrada.congelados_count
                                        }
                                        dessincronizado={
                                          entrada.dessincronizado
                                        }
                                        onClick={
                                          entrada.lote?.id
                                            ? () =>
                                                navigate(
                                                  `/producao/lotes/${entrada.lote.id}`,
                                                )
                                            : undefined
                                        }
                                      />
                                    </CardArrastavel>
                                  );
                                }
                                const a = entrada;
                                return (
                                  <CardArrastavel
                                    key={eid}
                                    $arrastavel={arrastavel}
                                    $origem={
                                      dragVisual
                                      && idEntrada(dragVisual.entrada) === eid
                                    }
                                    $shake={shakeId === eid}
                                    title={
                                      arrastavel && ehCheckpoint
                                        ? "Etapa-checkpoint: conclui por bipagem no terminal"
                                        : operavel && a.congelado
                                          ? "Ambiente congelado — descongele para operar"
                                          : undefined
                                    }
                                    {...handlersDrag}
                                    onClickCapture={aoClickCaptureCard}
                                  >
                                    <AmbienteCard
                                      densidade="compact"
                                      codigo={a.numero}
                                      nome={a.nome}
                                      pedidoNumero={a.pedido_numero}
                                      cliente={a.cliente_nome}
                                      faseLabel={
                                        FASE_INFO[a.fase]?.curto || a.fase
                                      }
                                      etapaLabel={coluna.label}
                                      loteNumero={a.lote_numero}
                                      onLoteClick={
                                        a.lote_id
                                          ? () =>
                                              navigate(
                                                `/producao/lotes/${a.lote_id}`,
                                              )
                                          : undefined
                                      }
                                      semLote={Boolean(a.sem_lote)}
                                      pecasTotal={a.pecas_total}
                                      progressoPct={a.progresso_pct}
                                      bufferPct={a.buffer?.pct ?? 0}
                                      bufferZona={a.buffer?.zona || "verde"}
                                      congelado={a.congelado}
                                      onClick={() => abrirAmbiente(a.id)}
                                    />
                                  </CardArrastavel>
                                );
                              })}
                            </ListaCards>
                          )}
                        </ColunaEtapa>
                      );
                    })}
                  </ColunasDaFase>
                </GrupoFase>
              ))}
            </BoardScroll>
          )
        ) : (
          <ZonaTabela>
            {erroLista && (
              <div style={{ padding: 12 }}>
                <LinhaErro>
                  <ErrorMsg style={{ flex: 1 }}>{erroLista}</ErrorMsg>
                  <SecondaryButton type="button" onClick={carregarLista}>
                    Tentar de novo
                  </SecondaryButton>
                </LinhaErro>
              </div>
            )}
            <TabelaFixa>
              <colgroup>
                <col style={{ width: "24%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Ambiente</th>
                  <th>Cliente</th>
                  <th>Pedido</th>
                  <th>Fase</th>
                  <th>Etapa atual</th>
                  <th>Buffer</th>
                  <th>Congelado</th>
                </tr>
              </thead>
              <tbody>
                {linhasVisiveis.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => abrirAmbiente(a.id)}
                    title={`${a.numero} · ${a.nome}`}
                  >
                    <td>
                      <Mono>{a.numero}</Mono>
                      {" · "}
                      {a.nome}
                    </td>
                    <td title={a.cliente_nome || ""}>
                      {a.cliente_nome || "–"}
                    </td>
                    <td>
                      <Mono>{a.pedido_numero}</Mono>
                    </td>
                    <td>
                      <Pill
                        $tone="purple"
                        title={FASE_INFO[a.fase]?.rotulo || a.fase_display}
                      >
                        {FASE_INFO[a.fase]?.curto || a.fase_display}
                      </Pill>
                    </td>
                    <td title={etapaPorAmbiente[a.id] || ""}>
                      {etapaPorAmbiente[a.id] ? (
                        <Pill $tone="neutral">{etapaPorAmbiente[a.id]}</Pill>
                      ) : (
                        "–"
                      )}
                    </td>
                    <td>
                      <DotZona $zona={a.buffer?.zona} aria-hidden="true" />
                      <Mono>{a.buffer?.pct ?? 0}%</Mono>
                    </td>
                    <td>
                      {a.congelado ? (
                        <Pill $tone="warning">Congelado</Pill>
                      ) : (
                        "–"
                      )}
                    </td>
                  </tr>
                ))}
                {lista !== null && linhasVisiveis.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                      {temFiltroAtivo
                        ? "Nenhum ambiente no recorte atual — ajuste a busca ou os filtros."
                        : "Nenhum ambiente em produção."}
                    </td>
                  </tr>
                )}
              </tbody>
            </TabelaFixa>
          </ZonaTabela>
        )}
      </Zona3>

      {/* Clone flutuante do card durante o arraste (pointer events). */}
      {dragVisual && (
        <GhostDrag
          ref={setGhostEl}
          style={{ width: dragVisual.largura }}
          aria-hidden="true"
        >
          {ehEntradaLote(dragVisual.entrada) ? (
            <LoteCard
              densidade="compact"
              numero={dragVisual.entrada.lote?.numero}
              origem={dragVisual.entrada.lote?.origem}
              sobraPercentual={dragVisual.entrada.lote?.sobra_percentual}
              ambientes={
                Array.isArray(dragVisual.entrada.ambientes)
                  ? dragVisual.entrada.ambientes
                  : null
              }
              ambientesCount={ambientesRepresentados(dragVisual.entrada)}
              bufferPct={dragVisual.entrada.buffer_pior?.pct ?? 0}
              bufferZona={dragVisual.entrada.buffer_pior?.zona || "verde"}
              pecasTotal={dragVisual.entrada.pecas_total}
              congeladosCount={dragVisual.entrada.congelados_count}
              dessincronizado={dragVisual.entrada.dessincronizado}
            />
          ) : (
            <AmbienteCard
              densidade="compact"
              codigo={dragVisual.entrada.numero}
              nome={dragVisual.entrada.nome}
              pedidoNumero={dragVisual.entrada.pedido_numero}
              cliente={dragVisual.entrada.cliente_nome}
              faseLabel={
                FASE_INFO[dragVisual.entrada.fase]?.curto
                || dragVisual.entrada.fase
              }
              etapaLabel={dragVisual.coluna.label}
              pecasTotal={dragVisual.entrada.pecas_total}
              progressoPct={dragVisual.entrada.progresso_pct}
              bufferPct={dragVisual.entrada.buffer?.pct ?? 0}
              bufferZona={dragVisual.entrada.buffer?.zona || "verde"}
              congelado={dragVisual.entrada.congelado}
            />
          )}
        </GhostDrag>
      )}

      {/* Confirmação do drag 1-adiante: soltar na próxima coluna =
          concluir a etapa ATUAL da entrada (ambiente OU lote em bloco). */}
      <SlideOverPanel
        open={Boolean(confirmacao)}
        onClose={() => setConfirmacao(null)}
        title={
          confirmacao && ehEntradaLote(confirmacao.entrada)
            ? "Concluir etapa do lote"
            : "Concluir etapa"
        }
        subtitle={
          confirmacao
            ? ehEntradaLote(confirmacao.entrada)
              ? `${confirmacao.entrada.lote?.numero} · `
                + `${ambientesRepresentados(confirmacao.entrada)} ambiente(s)`
              : `${confirmacao.entrada.numero} · ${confirmacao.entrada.nome}`
            : undefined
        }
        footer={
          <RodapeConfirmacao>
            <SecondaryButton
              type="button"
              onClick={() => setConfirmacao(null)}
            >
              Voltar
            </SecondaryButton>
            <AsyncButton
              silentError
              silentSuccess
              onClick={async () => {
                const { entrada, coluna } = confirmacao;
                // Card-LOTE (Lote C): a etapa é do próprio lote — a action
                // recebe o etapa_codigo da coluna e cascateia em bloco aos
                // membros; membro congelado/fora da etapa vem em `pulados`.
                if (ehEntradaLote(entrada)) {
                  // Sem silentError: 400 (checkpoint) / 403 (fase) saem no
                  // auto-toast global com o payload.detalhe do backend.
                  const resp = await concluirEtapaLote(
                    request, entrada.lote.id, coluna.codigo,
                  );
                  const concluidos = resp?.concluidos ?? 0;
                  const pulados = Array.isArray(resp?.pulados)
                    ? resp.pulados
                    : [];
                  toast.success(
                    `Etapa ${coluna.label} do lote ${entrada.lote.numero} `
                    + `concluída — ${concluidos} ambiente(s).`,
                  );
                  if (pulados.length > 0) {
                    toast.warning(
                      `${pulados.length} pulado(s): ${pulados
                        .map((p) => `${p.ambiente} (${p.motivo})`)
                        .join("; ")}`,
                    );
                  }
                  setConfirmacao(null);
                  await carregarPainel();
                  return;
                }
                // O card do board não expõe o id da EtapaExecucao — busca
                // o Detalhe e casa pela etapa da coluna de origem.
                const detalhe = await obterAmbiente(request, entrada.id);
                const etapa = (detalhe?.etapas || []).find(
                  (e) =>
                    e.codigo === coluna.codigo
                    && e.estado !== "NAO_APLICAVEL"
                    && e.estado !== "CONCLUIDA",
                );
                if (!etapa) {
                  toast.error(
                    `A etapa atual de ${entrada.numero} mudou desde o `
                    + "carregamento — painel atualizado.",
                  );
                  setConfirmacao(null);
                  await carregarPainel();
                  return;
                }
                // Sem silentError: um 400 do backend (ex.: fallback do
                // checkpoint de bipagem) sai no auto-toast com o detalhe.
                await concluirEtapa(request, entrada.id, etapa.id);
                toast.success(
                  `Etapa ${coluna.label} de ${entrada.numero} concluída.`,
                );
                setConfirmacao(null);
                await carregarPainel();
              }}
            >
              Concluir etapa
            </AsyncButton>
          </RodapeConfirmacao>
        }
      >
        {confirmacao
          && (ehEntradaLote(confirmacao.entrada) ? (
            <p style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5 }}>
              Concluir <strong>{confirmacao.coluna.label}</strong> do lote{" "}
              <strong>{confirmacao.entrada.lote?.numero}</strong> (
              {ambientesRepresentados(confirmacao.entrada)} ambientes)? A
              conclusão cascateia em bloco para as etapas dos ambientes
              membros; ambiente congelado ou fora da etapa é pulado e listado
              no resultado.
            </p>
          ) : (
            <p style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5 }}>
              Concluir <strong>{confirmacao.coluna.label}</strong> de{" "}
              <strong>
                {confirmacao.entrada.numero} · {confirmacao.entrada.nome}
              </strong>
              ? O ambiente avança para a próxima etapa aplicável do roteiro.
            </p>
          ))}
      </SlideOverPanel>
    </PainelPagina>
  );
}
